import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Share2, Check } from "lucide-react";
import TourPlayer from "@/components/TourPlayer";
import { useLanguage, LangSwitcher } from "@/context/LanguageContext";
import { TOURS } from "@/lib/tours";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";

export default function PublicTour() {
    const [started, setStarted] = useState(false);
    const [copied, setCopied] = useState(false);
    useLanguage();

    const share = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            toast.success(tr("Walkthrough link copied — share it with anyone"));
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error(tr("Copy failed — select the URL manually"));
        }
    };

    return (
        <div className="min-h-screen bg-[#04101e] text-white relative overflow-hidden" data-testid="public-tour-page">
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(ellipse 55% 60% at 80% 10%, #00B4D8 0%, transparent 60%), radial-gradient(ellipse 40% 45% at 8% 95%, #2DD4BF 0%, transparent 55%)" }} />
            <div className="relative max-w-3xl mx-auto px-6 py-10 min-h-screen flex flex-col">
                <div className="flex items-center justify-between">
                    <Link to="/" data-testid="tour-back-home" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/50 hover:text-[#7CD4EA] transition-colors">
                        <ArrowLeft className="w-3.5 h-3.5" /> {tr("Back to site")}
                    </Link>
                    <div className="flex items-center gap-2">
                        <LangSwitcher dark testid="tour-lang-switcher" />
                        <button data-testid="tour-share-btn" onClick={share}
                        className="inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-full border border-white/20 text-white/80 hover:bg-white/10 transition-colors">
                        {copied ? <Check className="w-3.5 h-3.5 text-[#06D6A0]" /> : <Share2 className="w-3.5 h-3.5" />}
                        {copied ? tr("Link copied") : tr("Share this walkthrough")}
                    </button>
                    </div>
                </div>

                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="flex-1 flex flex-col items-center justify-center text-center py-16">
                    <img src="/assets/logo-light.png" alt="LandLogic NexusOne" className="h-16 w-auto drop-shadow-xl" />
                    <div className="mt-8 inline-block text-[10px] tracking-[0.25em] uppercase font-bold text-[#7CD4EA] border border-[#7CD4EA]/30 rounded-full px-4 py-1.5">{tr("Self-running · Voice narrated · 2 minutes")}</div>
                    <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold font-heading tracking-tight leading-[1.05]">
                        {tr("The platform walkthrough")}
                    </h1>
                    <p className="mt-4 text-slate-300 max-w-xl leading-relaxed">
                        {tr("Sit back — this guided tour plays itself, with voice narration, from the public register to the planner workspace, applicant portal, financials and AI. No account needed.")}
                    </p>
                    <button data-testid="tour-start-btn" onClick={() => setStarted(true)}
                        className="mt-9 inline-flex items-center gap-2.5 px-8 py-4 rounded-[14px] bg-[#00B4D8] hover:bg-[#00c3eb] text-white font-bold transition-all hover:shadow-[0_16px_40px_-10px_rgba(0,180,216,0.7)] hover:-translate-y-0.5 active:scale-[0.98]">
                        {tr("Start the walkthrough")}
                    </button>
                    <div className="mt-5 text-[11px] text-white/40 font-data">{tr("Best with sound on · works in any modern browser")}</div>
                </motion.div>

                <div className="text-center text-[11px] text-white/30 pb-2">{tr("© 2026 City of Brampton · Powered by LandLogic NexusOne")}</div>
            </div>

            <TourPlayer steps={TOURS.general.steps} title={tr("Platform walkthrough")} open={started} onClose={() => setStarted(false)} />
        </div>
    );
}
